type ToastEntry<T> = { id: number; value: T; persistent: boolean }
type ActiveEntry<T> = ToastEntry<T> & { active?: boolean; close?: () => void; closing?: boolean }
type ToastDriver<T> = (entry: ToastEntry<T>, removed: () => void) => () => void

// Queue before admission to Kobalte: its dismiss API does not remove unmounted
// entries. Only visible entries enter that store; their normal close lifecycle
// removes them. Persistent notices never compete with disposable notices.
export function createToastQueue<T>(capacity = 10) {
  const entries = new Map<number, ActiveEntry<T>>()
  let nextID = 0
  let driver: ToastDriver<T> | undefined
  let ordinaryLimit = 3
  let pumping = false
  const pump = () => {
    if (!driver || pumping) return
    pumping = true
    try {
      for (const persistent of [true, false]) {
        const limit = persistent ? 3 : ordinaryLimit
        let active = [...entries.values()].filter(entry => entry.persistent === persistent && entry.active).length
        for (const entry of entries.values()) {
          if (active >= limit) break
          if (entry.persistent !== persistent || entry.active) continue
          entry.active = true
          active++
          entry.close = driver(entry, () => { entries.delete(entry.id); pump() })
          if (entry.closing) entry.close()
        }
      }
    } finally { pumping = false }
  }
  const dismiss = (id: number) => {
    const entry = entries.get(id)
    if (!entry) return id
    if (!entry.active) entries.delete(id)
    else { entry.closing = true; entry.close?.() }
    pump()
    return id
  }
  const clear = () => {
    // Keep exiting entries in the admission count until their library roots
    // unmount. Repeated clear/show cycles must not queue hidden library entries.
    for (const entry of entries.values()) if (!entry.active) entries.delete(entry.id)
    for (const id of [...entries.keys()]) dismiss(id)
  }
  return {
    show(value: T, persistent = false) {
      const id = nextID++
      if (!persistent) {
        const ordinary = [...entries.values()].filter(entry => !entry.persistent)
        if (ordinary.length >= capacity) {
          const queued = ordinary.find(entry => !entry.active)
          if (!queued) return id
          entries.delete(queued.id)
        }
      }
      entries.set(id, { id, value, persistent })
      pump()
      return id
    },
    dismiss,
    clear,
    size: () => entries.size,
    connect(show: ToastDriver<T>, limit: number) {
      driver = show
      ordinaryLimit = Math.min(capacity, limit)
      pump()
      return {
        setLimit(value: number) {
          ordinaryLimit = Math.min(capacity, value)
          const active = [...entries.values()].filter(entry => entry.active && !entry.persistent && !entry.closing)
          for (const entry of active.slice(ordinaryLimit)) dismiss(entry.id)
          pump()
        },
        dispose() {
          if (driver !== show) return
          driver = undefined
          clear()
          entries.clear()
        },
      }
    },
  }
}
